import { useMutation, useQuery } from '@apollo/client';
import { useIsMobile } from '@app/app/hooks/useIsMobile';
import { get } from '@app/app/utils/get';
import { getFieldsFromSurveys } from '@app/application/utils/survey';
import { questionnaireConfig } from '@app/questionnaire/configs';
import {
  questionnaireGetOne,
  QUESTIONNAIRE_CREATE_NEW_JOURNEY,
  QUESTIONNAIRE_LOCK_UPDATE,
  QUESTIONNAIRE_UNLOCK_UPDATE,
} from '@app/questionnaire/gql';
import { Questionnaire } from '@app/questionnaire/types/Questionnaire';
import { ColorCodeStatusEnum, QuestionnaireStatusEnum } from '@app/questionnaire/constants';
import { Breadcrumbs } from '@app/ui/breadcrumbs';
import { Label } from '@app/ui/label';
import { useConfirmation, useModal } from '@app/ui/modal';
import { Dropdown } from '@app/ui/dropdown';
import { ApplicationSwitcher } from '@app/shared/components/ApplicationSwitcher';
import { error as showError, success as showSuccess } from '@app/snackbars';
import { questionnaireGeneralInfoItem, USER_STATUS_COLOR } from '@app/user/constants';
import { Divider, Grid, InputLabel, Paper, Tab, Tabs, Typography } from '@mui/material';
import { Box } from '@mui/system';
import dayJs from 'dayjs';
import { appConfig } from '@app/app';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader } from '@app/ui/loader';
import { ApplicationTabIcon } from '@app/application/components/item/icons';
import { PriorityTagsCell } from '@app/application/components/item/PriorityTagsCell';
import { QuestionnaireList } from './QuestionnaireList';
import { QuestionnaireUploadForm } from '../upload/QuestionnaireUploadForm';
import { QuestionnaireUpdatePdfCoverModal } from '../pdf/QuestionnaireUpdatePdfCoverModal';
import { QuestionnaireExportPdfDropdown } from '../QuestionnaireExportPdfDropdown';

export const AdminQuestionnaireItem = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const routerParams = useParams();
  const isMobile = useIsMobile();
  const { isOpen, close, show } = useModal();
  const { entity, ...params } = useParams<{ id?: string; entity: 'surrogates' | 'parent' }>();

  const id = Number(params.id);

  const [tab, setTab] = useState(0);

  const fields = useMemo(() => {
    const { surveyFields } = getFieldsFromSurveys(questionnaireConfig.surveys);

    // SAA-135 slice 3 — `priorityTags` is NOT a survey element, so `getFieldsFromSurveys` never
    // picks it up; it must be appended by hand or it arrives `undefined` with no error (no codegen).
    // SAA-112 P-K — sibling questionnaires feed the internal switcher (AC 3.1).
    // ai-failure-handling PLAN P5 — `colorCodeStatus` is the parked-state signal for the colour cell
    // below, same "append by hand" reason as `priorityTags`.
    return [
      ...surveyFields,
      'status',
      'user.email',
      'boxName',
      'priorityTags',
      'colorCodeStatus',
      'isUnlocked',
      'user.questionnaires.id',
      'user.questionnaires.createdAt',
    ];
  }, []);

  const { data, loading, refetch } = useQuery<{ questionnaireGetOne?: Questionnaire }>(questionnaireGetOne(fields), {
    variables: { id },
    fetchPolicy: 'no-cache',
  });

  const userName = `${data?.questionnaireGetOne?.firstName || ''} ${data?.questionnaireGetOne?.lastName || ''}`.trim();
  // SAA-135, dev ruling (SPEC §3.7 H4): NULL/empty boxName falls back to the current name — staff
  // always get a usable title, never a blank header.
  const headerName = data?.questionnaireGetOne?.boxName || userName;

  // SAA-112 S3 — "New App": staff-initiated only, behind a confirmation (AC 1). Every `/admin/*`
  // route is already `isInternal`-gated at the router (`_private.tsx`) — no extra role check here.
  const [createNewJourney, { loading: isCreatingNewJourney }] = useMutation<{
    questionnaireCreateNewJourney: Questionnaire;
  }>(QUESTIONNAIRE_CREATE_NEW_JOURNEY);

  const [confirmNewApp] = useConfirmation({
    title: t('application.button.newApp.modal.title'),
    submitButtonText: t('application.button.newApp.modal.confirm'),
    isLoading: isCreatingNewJourney,
  });

  const handleNewApp = useCallback(
    () =>
      confirmNewApp({
        description: t('application.button.newApp.modal.description', { name: headerName }),
        onSubmit: async closeModal => {
          try {
            const { data: mutationData } = await createNewJourney({ variables: { input: { id } } });
            const newId = mutationData?.questionnaireCreateNewJourney?.id;

            closeModal();

            if (newId) {
              navigate(`/admin/${entity}/${newId}`);
            }
          } catch (e) {
            showError(t('general.error.somethingWentWrong'));
          }
        },
      }),
    [confirmNewApp, createNewJourney, entity, headerName, id, navigate, t],
  );

  // SAA-128 — staff-only unlock/lock (AC-1/AC-5); absent from the menu on a still-editable first
  // questionnaire (AC-3, checked at the render site below). IP editability is the hardcoded
  // `applicants` comparison (no per-tenant `isEditableStatus` on this side — spec non-goal), so the
  // menu's visibility check mirrors that, not the GC helper.
  const [unlockQuestionnaire, { loading: isUnlockLoading }] = useMutation<{
    questionnaireUnlockUpdate: Questionnaire;
  }>(QUESTIONNAIRE_UNLOCK_UPDATE);
  const [lockQuestionnaire, { loading: isLockLoading }] = useMutation<{ questionnaireLockUpdate: Questionnaire }>(
    QUESTIONNAIRE_LOCK_UPDATE,
  );

  const [confirmUnlockLock] = useConfirmation({ isLoading: isUnlockLoading || isLockLoading });

  const isUnlocked = data?.questionnaireGetOne?.isUnlocked;

  const handleUnlockLock = useCallback(() => {
    const key = isUnlocked ? 'lockProfile' : 'unlockProfile';
    const mutate = isUnlocked ? lockQuestionnaire : unlockQuestionnaire;

    confirmUnlockLock({
      title: t(`application.button.${key}.modal.title`),
      description: t(`application.button.${key}.modal.description`),
      submitButtonText: t(`application.button.${key}.modal.confirm`),
      onSubmit: async closeModal => {
        try {
          await mutate({ variables: { input: { id } } });

          closeModal();
          refetch();
          showSuccess(t(`application.button.${key}.successMessage`));
        } catch (e) {
          showError(t('general.error.somethingWentWrong'));
        }
      },
    });
  }, [confirmUnlockLock, id, isUnlocked, lockQuestionnaire, refetch, t, unlockQuestionnaire]);

  // SAA-112 P-K — switcher: sibling questionnaires of the SAME user (AC 3.1/3.4). Numbers entries
  // by creation order; display order (below) is newest-first. No "Latest" tag — removed
  // deliberately in the design pass (D12/R7).
  const siblingQuestionnairesAsc = useMemo(
    () =>
      [...(data?.questionnaireGetOne?.user?.questionnaires ?? [])]
        .filter((sibling): sibling is Questionnaire & { id: number } => sibling.id !== undefined)
        .sort((a, b) => +new Date(a.createdAt ?? 0) - +new Date(b.createdAt ?? 0)),
    [data?.questionnaireGetOne?.user?.questionnaires],
  );

  const switcherOptions = useMemo(
    () =>
      siblingQuestionnairesAsc
        .map((sibling, index) => ({
          id: sibling.id,
          name: t('application.field.switcher.applicationName', { n: index + 1 }),
          meta: t('application.field.switcher.created', {
            date: dayJs(sibling.createdAt).format(appConfig.format.date),
          }),
        }))
        .reverse(),
    [siblingQuestionnairesAsc, t],
  );

  const breadcrumbs = useMemo(
    () => [
      {
        title: t('user.page.parentAdminList.title'),
        to: `/admin/${entity}`,
      },
      {
        title: headerName,
      },
    ],
    [t, headerName, entity],
  );

  const handleSurveyClick = useCallback(
    (survey: string) => navigate(`/admin/${routerParams.entity}/${id}/${survey}`),
    [id, routerParams],
  );

  return (
    <>
      <Breadcrumbs options={breadcrumbs} />
      <Box mb={3} display="flex" alignItems="center" flexWrap="wrap">
        <Typography variant="h1">{headerName}</Typography>
        {data?.questionnaireGetOne?.status && (
          <Label
            sx={{ ml: 2, mt: 0.5, textTransform: 'capitalize' }}
            text={t(`questionnaire.field.status.option.${data?.questionnaireGetOne?.status}`)}
            color={USER_STATUS_COLOR[data?.questionnaireGetOne?.status]}
          />
        )}
        <Box sx={{ ml: 'auto', mt: 0.5 }}>
          <ApplicationSwitcher
            options={switcherOptions}
            value={id}
            onChange={newId => navigate(`/admin/${entity}/${newId}`)}
          />
        </Box>
      </Box>

      <Paper
        sx={{
          wordWrap: 'break-word',
          borderRadius: {
            md: '20px',
          },
          p: {
            xs: 2,
            sm: 3,
            lg: 5,
          },
          mb: 5,
        }}
        variant="outlined"
        elevation={0}
      >
        <Typography variant="h3" sx={{ mb: 3 }}>
          {t('user.page.single.information')}
        </Typography>

        <Grid container direction={isMobile ? 'column' : 'row'} wrap={isMobile ? 'wrap' : 'nowrap'}>
          {data?.questionnaireGetOne &&
            questionnaireGeneralInfoItem.map((item, index) => {
              const lastChild = index === questionnaireGeneralInfoItem.length - 1;

              return (
                <Fragment key={item}>
                  <Grid item sx={{ flexGrow: 1, minWidth: 0 }}>
                    <InputLabel sx={{ mb: 1.5 }}>{t(`questionnaire.field.generalInfo.${item}.label`)}</InputLabel>
                    <div>
                      {/* SAA-179 reverses SAA-135 D-B: empty renders '-', like the GC card.
                      PriorityTagsCell renders '-' itself when the tag list is empty. */}
                      {item === 'colorCode' ? (
                        <PriorityTagsCell
                          tags={data.questionnaireGetOne?.priorityTags}
                          colorCodeFailed={data.questionnaireGetOne?.colorCodeStatus === ColorCodeStatusEnum.failed}
                        />
                      ) : (
                        get(data.questionnaireGetOne || {}, item) || '-'
                      )}
                    </div>
                  </Grid>

                  {!lastChild && (
                    <Divider
                      sx={{
                        my: {
                          xs: 1,
                          sm: 0,
                        },
                        mx: {
                          sm: 2,
                          lg: 3,
                        },
                      }}
                      orientation={isMobile ? 'horizontal' : 'vertical'}
                      flexItem
                    />
                  )}
                </Fragment>
              );
            })}
        </Grid>
      </Paper>

      {/* SAA-91 — one tab (Watchlist is ruled out for IPs, second tab arrives with SAA-105). The
      idle (`#F9EDF5` -> `primary.light`) style lives on `.MuiTab-root`; white bg + no bottom
      border move to `&.Mui-selected` (a bar-level bgcolor would paint both tabs white). The `-1px`
      root overlap and the scroller fix are untouched: MUI's scroller is `overflow: hidden` and
      clips the overlap if it sits on the Tab instead. */}
      <Tabs
        value={tab}
        onChange={(_event, value: number) => setTab(value)}
        aria-label={t('user.page.singleParent.subtitle') ?? undefined}
        sx={{
          minHeight: '52px',
          width: 'fit-content',
          position: 'relative',
          zIndex: 1,
          mb: '-1px',
          '& .MuiTabs-scroller': { overflow: 'visible !important' },
          '& .MuiTabs-indicator': { display: 'none' },
          '& .MuiTab-root': {
            minHeight: '52px',
            textTransform: 'none',
            fontWeight: 400,
            color: 'text.primary',
            bgcolor: 'primary.light',
            border: '1px solid',
            borderColor: 'grey.500',
            // `borderRightWidth`, not `borderRight` — the shorthand resets the colour to
            // `currentColor` and repaints that edge in the text colour.
            borderRadius: 0,
            borderRightWidth: 0,
            '&:first-of-type': { borderTopLeftRadius: '16px' },
            '&:last-of-type': { borderTopRightRadius: '16px', borderRightWidth: '1px' },
            // Design's `16px 24px` (§3 Q3b, ruled at the gate) — one shared rule, so the
            // Application tab's width moves too, deliberately, not as a side effect of tab #2.
            padding: '16px 24px',
            gap: 1,
            '& .MuiTab-iconWrapper': { fontSize: 20, m: 0 },
            // Without this MUI paints the selected tab (label and icon) `primary.main`.
            '&.Mui-selected': {
              color: 'text.primary',
              fontWeight: 600,
              bgcolor: 'background.paper',
              borderBottom: 'none',
            },
          },
        }}
      >
        <Tab
          id="application-tab-0"
          aria-controls="application-tabpanel-0"
          icon={<ApplicationTabIcon />}
          iconPosition="start"
          label={t('application.tab.application')}
        />
      </Tabs>

      <Paper
        sx={{
          borderRadius: '0 16px 16px 16px',
          p: {
            xs: 2,
            sm: 3,
            lg: 5,
          },
          mb: 5,
        }}
        variant="outlined"
        elevation={0}
      >
        <div role="tabpanel" id="application-tabpanel-0" aria-labelledby="application-tab-0" hidden={tab !== 0}>
          {tab === 0 && (
            <>
              {isOpen && <QuestionnaireUpdatePdfCoverModal id={id} open={isOpen} onClose={close} />}

              {data?.questionnaireGetOne && (
                <QuestionnaireList
                  item={data.questionnaireGetOne}
                  onClick={handleSurveyClick}
                  isAdmin
                  actions={
                    <>
                      <QuestionnaireExportPdfDropdown
                        questionnaireId={id}
                        label={t('application.button.exportPdf.name')}
                        arrow
                        sx={{ height: 36 }}
                      />

                      {/* SAA-112 design pass — "Actions" (ISC.Button contained/primaryLight) replaces the
                      standalone "Update profile highlights" button; "Initiate a new journey" joins it. */}
                      <Dropdown
                        label={t('application.button.actions.name')}
                        color="primaryLight"
                        arrow
                        popperWidth={340}
                        popperPlacement="bottom-end"
                        popperOffset={8}
                        sx={{ minWidth: 120, height: 36 }}
                        activeSx={{ bgcolor: 'primaryLight.dark', '&:hover': { bgcolor: 'primaryLight.dark' } }}
                        paperSx={{
                          py: 0,
                          my: 0,
                          border: '1px solid #EEF0F3',
                          boxShadow: '0 4px 16px rgba(51,51,51,0.16)',
                          overflow: 'hidden',
                        }}
                        itemSx={{
                          justifyContent: 'flex-start',
                          py: 2,
                          px: 2.5,
                          my: 0,
                          borderRadius: 0,
                          width: '100%',
                          fontSize: 16,
                          fontWeight: 400,
                          color: 'text.primary',
                          textAlign: 'left',
                          borderBottom: '1px solid #EEF0F3',
                          '&:last-of-type': { borderBottom: 'none' },
                          '&:hover': { bgcolor: '#F7F8F9' },
                        }}
                        options={[
                          { id: 'highlights', name: t('application.button.updateProfilePdf.name'), onClick: show },
                          { id: 'newJourney', name: t('application.button.newApp.name'), onClick: handleNewApp },
                          // AC-3 — absent while the questionnaire is still editable by status alone.
                          ...(data?.questionnaireGetOne?.status &&
                          data.questionnaireGetOne.status !== QuestionnaireStatusEnum.applicants
                            ? [
                                {
                                  id: 'unlockLock',
                                  name: t(
                                    isUnlocked
                                      ? 'application.button.lockProfile.name'
                                      : 'application.button.unlockProfile.name',
                                  ),
                                  onClick: handleUnlockLock,
                                },
                              ]
                            : []),
                        ]}
                      />
                    </>
                  }
                />
              )}

              <Box mt={4}>
                <Paper
                  sx={{
                    p: {
                      xs: 2,
                      sm: 3,
                      lg: 5,
                    },
                    borderRadius: '16px',
                  }}
                  variant="outlined"
                  elevation={0}
                >
                  <Typography variant="h4" mb={3}>
                    {t('application.field.photos.subtitle')}
                  </Typography>

                  {data?.questionnaireGetOne && <QuestionnaireUploadForm questionnaireId={id} />}
                </Paper>
              </Box>
            </>
          )}
        </div>
      </Paper>

      <Loader isLoading={loading} />
    </>
  );
};
